import { useContext, useState } from 'react';

import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  IconButton,
  Button,
  Hidden,
  Tooltip,
  styled,
  useTheme
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import TourOutlinedIcon from '@mui/icons-material/TourOutlined';
import Logo from 'src/components/LogoSign';
import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import HeaderLanguage from './Userbox/HeaderLanguage';
import HeaderTheme from './Userbox/HeaderTheme';
import SubscriptionAlert from 'src/components/SubscriptionAlert/SubscriptionAlert';
import { runMainTour } from 'src/utils/mainTour';
import { useTranslation } from 'react-i18next';


const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 1.5)};
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        width: 100%;
        max-width: 100vw;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        overflow-x: hidden;
        box-sizing: border-box;
        
        @media (min-width: ${theme.breakpoints.values.sm}px) {
            padding: ${theme.spacing(0, 2)};
        }
        
        @media (min-width: ${theme.breakpoints.values.md}px) {
            padding: ${theme.spacing(0, 3)};
        }
        
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            padding: ${theme.spacing(0, 4)};
        }
`
);

const LogoBox = styled(Box)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 120px;
    order: 1;
    
    @media (min-width: ${theme.breakpoints.values.sm}px) {
        width: 70px;
    }
    
    @media (min-width: ${theme.breakpoints.values.md}px) {
        width: 80px;
    }
    
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        width: 150px;
    }

    img, svg {
        width: 100%;
        height: auto;
        max-height: 70px;
        object-fit: contain;
    }

    
`
);

const MiddleSection = styled(Stack)(
  ({ theme }) => `
    display: none;
    flex: 1;
    justify-content: center;
    max-width: 600px;
    margin-inline: ${theme.spacing(2)};
    order: 2;
    min-width: 0;
    
    @media (min-width: ${theme.breakpoints.values.md}px) {
        display: flex;
    }

    [dir="rtl"] & {
        order: 2;
    }
`
);

const ActionsContainer = styled(Box)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    flex-shrink: 0;
    order: 3;
    
    @media (min-width: ${theme.breakpoints.values.sm}px) {
        gap: ${theme.spacing(1)};
    }
    
    @media (min-width: ${theme.breakpoints.values.md}px) {
        gap: ${theme.spacing(1.5)};
    }

`
);

const IconSlot = styled(Box)(
  () => `
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 36px;
    
    > * { 
        margin: 0 !important; 
    }
`
);

const MenuToggleBox = styled(Box)(
  () => `
    display: flex;
    align-items: center;
    flex-shrink: 0;
`
);

function Header() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);

  return (
    <HeaderWrapper
      sx={{
        ...theme.appVars,
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
    >
      <LogoBox>
        <Logo />
      </LogoBox>

      <MiddleSection
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        spacing={2}
      >
        <HeaderMenu />
      </MiddleSection>

      <ActionsContainer>
        <MenuToggleBox
          sx={{
            display: { lg: 'none', xs: 'flex' }
          }}
        >
          <Tooltip arrow title="Toggle Menu">
            <IconButton color="primary" onClick={toggleSidebar} size="small">
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </MenuToggleBox>

        <SubscriptionAlert />

        <Tooltip arrow title={t('tour.restart_button_tooltip')}>
          <Button
            size="small"
            startIcon={<TourOutlinedIcon fontSize="small" />}
            onClick={() => runMainTour(i18n)}
            sx={{
              flexShrink: 0,
              color: theme.colors.primary.main,
              '&:hover': {
                color: theme.colors.primary.main,
                background: alpha(theme.colors.primary.main, 0.08)
              }
            }}
          >
            <Hidden smDown>{t('tour.button_label')}</Hidden>
          </Button>
        </Tooltip>

        <IconSlot>
          <HeaderButtons notifications={notifications} />
        </IconSlot>

        <IconSlot>
          <HeaderTheme />
        </IconSlot>

        <IconSlot>
          <HeaderLanguage />
        </IconSlot>

        <IconSlot sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <HeaderUserbox />
        </IconSlot>
      </ActionsContainer>
    </HeaderWrapper>
  );
}

export default Header;
